import React, { useState, useEffect, useRef, useContext, useMemo, useCallback, startTransition } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDisclosure, IconButton, Tooltip, useToast } from '@chakra-ui/react';
import { FiActivity } from 'react-icons/fi';
import ChatLayout from '../../components/chat/ChatLayout';
import ChatInputBar from '../../components/chat/ChatInputBar';
import DocumentUploadZone from '../../components/chat/DocumentUploadZone';
import useDocumentUpload from '../../hooks/useDocumentUpload';
import MarkdownRender from '../../utils/MarkdownMathjax';
import TypingIndicator from '../../components/chat/TypingIndicator';
import { useConversation } from '../../contexts/ConversationContext';
import { MessageSenderRoleEnum } from '../../types/api';
import { AuthContext } from '../../contexts/AuthContext';
import ConnectionStatusIndicator from '../../components/chat/ConnectionStatusIndicator';
import ArtifactLayout from '../../components/debug/ArtifactLayout';
import ArtifactDebugPanel from '../../components/debug/PersonalizationDebugPanel/ArtifactDebugPanel';
import { useDebugStore } from '../../stores/debugStore';
import '../../components/chat/ChatRuntime.css';
import './ChatPage.css';
import { useZustandChat } from '../../hooks/chat/useZustandChat';
import { ChatProviderWrapper } from '../../components/chat/ChatProviderWrapper';
import StreamRequestIdBadge from '../../components/chat/StreamRequestIdBadge';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';
// Split UI: we only keep a button placeholder; no panel/store
import { threadService } from '../../api/threadService';
import { useChatStore } from '../../stores/chatStore';
import { Copy, GitBranch } from 'lucide-react';
import SplitChatIcon from '../../components/icons/SplitChatIcon';
import MessageFeedback from '../../components/chat/MessageFeedback';
import { resolveThreadMessageId as resolveThreadMsgId } from '../../utils/threadMessageResolver';
import SplitThinkingIndicator from '../../components/chat/SplitThinkingIndicator';

function isUUIDLike(id?: string) {
  if (!id) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
}

function normalizeText(s?: string): string {
  return (s || '').replace(/\s+/g, ' ').trim();
}

const ChatContent: React.FC = React.memo(() => {
  // Render loop detection
  const renderCountRef = useRef(0);
  const lastRenderTimeRef = useRef(Date.now());
  const [isContextReady, setIsContextReady] = useState(false);
  
  useEffect(() => {
    renderCountRef.current++;
    const now = Date.now();
    const timeSinceLastRender = now - lastRenderTimeRef.current;
    
    // Detect rapid re-renders (more than 10 renders in 100ms)
    if (renderCountRef.current > 10 && timeSinceLastRender < 100) {
      // console.error('[ChatPage] RENDER LOOP DETECTED!', {
      //   renderCount: renderCountRef.current,
      //   timeSinceLastRender,
      //   timestamp: new Date().toISOString()
      // });
      // Reset counter to prevent continuous warnings
      renderCountRef.current = 0;
    }
    
    lastRenderTimeRef.current = now;
    
    // Reset counter after 1 second of no renders
    const resetTimer = setTimeout(() => {
      renderCountRef.current = 0;
    }, 1000);
    
    return () => clearTimeout(resetTimer);
  });
  
  
  const { conversationId } = useParams<{ conversationId?: string }>();
  const isTestMode = typeof window !== 'undefined' && window.location.pathname.startsWith('/chat-test');
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const toast = useToast();
  
  
  const {
    currentConversation,
    createConversation,
    selectConversation,
    updateConversation,
    clearCurrentConversation,
    isLoading: conversationLoading,
  } = useConversation();
  
  // Use current conversation public_id, or fall back to the route param while context loads
  // IMPORTANT: Only use conversationId when it matches currentConversation to avoid mismatches
  // Use the route param directly when present to avoid gating on context hydration.
  // This ensures direct navigation to /chat/:public_id streams immediately.
  // IMPORTANT: On base /chat (no :conversationId), do NOT fall back to
  // currentConversation. We want a blank state until first send.
  const convRef = (
    isTestMode
      ? (conversationId || 'public-test')
      : (conversationId || '')
  ) as string;
  
  
  // Use fixed Zustand chat with stable selectors
  // Only initialize chat when we have a valid conversation reference
  const {
    messages: messagesRaw,
    sendMessage: sendMessageToAPI,
    isLoading: messagesLoading,
    isSending,
    isStreaming,
    error: messagesError
  } = useZustandChat({
    // Pass empty string if no valid conversation to prevent errors
    conversationId: convRef || '',
    onStreamStart: () => {
      setConnectionError(false);
    },
    onStreamError: (error) => {
      setConnectionError(true);
    }
  });

  // On base /chat (no :conversationId), clear any previous chat state and
  // current conversation so the page renders as a fresh chat.
  useEffect(() => {
    if (!isTestMode && !conversationId) {
      try {
        useChatStore.getState().clearAllMessages();
      } catch {}
      try {
        clearCurrentConversation();
      } catch {}
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, isTestMode]);
  
  // Defensive: ensure messages is always an array to avoid transient undefined during route switches
  const messages = useMemo(() => Array.isArray(messagesRaw) ? messagesRaw : [], [messagesRaw]);
  const messagesCount = Array.isArray(messages) ? messages.length : 0;

  
  // Using stable Zustand selectors - no infinite loops!

  const [isInputFullScreen, setIsInputFullScreen] = useState(false);
  // Distinguish main vs side streaming
  const currentStreamThreadId = useChatStore((s) => s.currentStream?.threadId);
  const currentStreamIsSide = useChatStore((s) => !!s.currentStream?.sideStream);
  const [connectionError, setConnectionError] = useState(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const isSubmittingRef = useRef<boolean>(false);
  const lastScrollTsRef = useRef<number>(0);

  // Debug panel state and controls
  const { isOpen: isDebugOpen, onOpen: onDebugOpen, onClose: onDebugClose } = useDisclosure();
  const { setIsOpen: setDebugStoreOpen } = useDebugStore();
  const canAccessDebugPanel = !!user?.is_superuser;
  const debugOverride = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qp = params.get('debug');
      const ls = localStorage.getItem('enableDebugPanel');
      // Only enable when explicitly requested (query param or localStorage), not automatically in dev
      return qp === '1' || qp === 'true' || ls === '1' || ls === 'true';
    } catch {
      return false;
    }
  }, []);
  const canShowDebugToggle = canAccessDebugPanel || debugOverride;
  // Simple mode: hide extra chrome for a cleaner chat UI (query ?simple=1 or localStorage.simpleChat=1)
  const simpleMode = useMemo(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const qp = p.get('simple');
      const ls = localStorage.getItem('simpleChat');
      return qp === '1' || qp === 'true' || ls === '1' || ls === 'true';
    } catch {
      return false;
    }
  }, []);
  // Dev-only: flag to enable/disable /chat-test mock seeding (default: disabled)
  const enableTestSeed = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qp = params.get('seedMock'); // e.g., ?seedMock=1 to force enable
      if (qp != null) return qp === '1' || qp === 'true';
      const ls = localStorage.getItem('chatTestSeed');
      if (ls != null) return ls === '1' || ls === 'true';
      const env = (import.meta as any)?.env?.VITE_ENABLE_CHAT_TEST_SEED;
      return env === '1' || env === 'true';
    } catch {
      return false;
    }
  }, []);
  const autoOpenedRef = useRef(false);
  const [toggleRightOffset, setToggleRightOffset] = useState<string>('20px');
  const [artifactRightOffset, setArtifactRightOffset] = useState<string>('0px');
  // Map UI message key -> resolved server thread message UUID
  const [resolvedThreadIdByKey, setResolvedThreadIdByKey] = useState<Record<string, string>>({});
  // Split UI state
  const [isSplitOpen, setIsSplitOpen] = useState<boolean>(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const qp = p.get('split');
      const ls = localStorage.getItem('splitChat');
      return qp === '1' || qp === 'true' || ls === '1' || ls === 'true';
    } catch { return false; }
  });
  // Split width CSS var is updated imperatively to avoid React re-renders during drag
  const containerRef = useRef<HTMLDivElement>(null);

  // Split bubbles are always enabled; ?split=1 just auto-opens the panel
  // Split bookmark visibility/enablement rules
  const hasMessages = messagesCount > 0;
  const shouldRenderSplitBookmark = !isSplitOpen && hasMessages;

  // Per-message DOM refs for scroll linking
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const setMsgRef = useCallback((id: string) => (el: HTMLDivElement | null) => {
    if (!id) return;
    messageRefs.current[id] = el;
  }, []);

  const scrollToMainMessage = useCallback((msgId: string) => {
    const target = messageRefs.current[msgId];
    if (target && 'scrollIntoView' in target) {
      try { (target as any).scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch {}
    }
  }, []);

  // Split bubble threads state (mocked per-bubble mini-chat for now)
  type BubbleMsg = { id: string; role: 'user' | 'assistant'; content: string };
  type BubbleThread = { id: string; fromMessageId: string; title: string; messages: BubbleMsg[]; isOpen: boolean; realThreadId?: string; anchorServerId?: string; creationError?: string; pending?: boolean; lastDraftSnapshot?: string };
  const [bubbleThreads, setBubbleThreads] = useState<BubbleThread[]>([]);
  const [activeBubbleId, setActiveBubbleId] = useState<string | null>(null);
  // Persist split panel state per-conversation across refreshes
  const bubblesHydratingRef = useRef(false);
  const persistSplitState = useCallback((conv: string, bubbles: BubbleThread[], activeId: string | null) => {
    try {
      if (!conv) return;
      // Persist minimal metadata only (messages reconstructed from ThreadView)
      const payload = {
        activeId,
        bubbles: bubbles.map(b => ({
          id: b.id,
          fromMessageId: b.fromMessageId,
          title: b.title,
          realThreadId: b.realThreadId,
          anchorServerId: b.anchorServerId,
          isOpen: b.isOpen,
        })),
      };
      localStorage.setItem(`splitThreads:${conv}`, JSON.stringify(payload));
    } catch {}
  }, []);
  const loadSplitState = useCallback(async (conv: string) => {
    if (!conv) return;
    let raw: any = null;
    try { raw = localStorage.getItem(`splitThreads:${conv}`); } catch {}
    if (!raw) return;
    let data: any;
    try { data = JSON.parse(raw); } catch { return; }
    if (!data || !Array.isArray(data.bubbles)) return;
    bubblesHydratingRef.current = true;
    try {
      // Seed skeletons
      const skeletons: BubbleThread[] = data.bubbles.map((s: any) => ({
        id: String(s.id || `split-${Date.now()}-${Math.random().toString(36).slice(2,6)}`),
        fromMessageId: String(s.fromMessageId || ''),
        title: String(s.title || 'Split'),
        messages: [],
        isOpen: Boolean(s.isOpen),
        realThreadId: s.realThreadId || undefined,
        anchorServerId: s.anchorServerId || undefined,
        pending: false,
        lastDraftSnapshot: '',
      }));
      setBubbleThreads(skeletons);
      setActiveBubbleId(data.activeId || null);
      // Hydrate messages for each real thread
      for (const sk of skeletons) {
        if (!sk.realThreadId) continue;
        try {
          const view = await threadService.getThreadView(sk.realThreadId);
          const msgs = Array.isArray(view?.messages) ? view.messages : [];
          const hydrated: BubbleMsg[] = msgs.map((m: any, idx: number) => ({
            id: `${sk.id}-srv-${idx}`,
            // Treat anything not explicitly 'user' as 'assistant' to ensure replies render
            role: (String(m.role).toLowerCase() === 'user' ? 'user' : 'assistant'),
            content: (m?.content && (m as any).content?.text) || JSON.stringify(m?.content || {}),
          }));
          setBubbleThreads(prev => prev.map(b => b.id === sk.id ? { ...b, messages: hydrated } : b));
        } catch {}
      }
    } finally {
      bubblesHydratingRef.current = false;
    }
  }, []);
  // Track when a side-bubble initiated the current stream so we can mirror tokens inside the bubble
  const [splitStreamingForBubbleId, setSplitStreamingForBubbleId] = useState<string | null>(null);
  const bubbleScrollRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const setBubbleRef = useCallback((id: string) => (el: HTMLDivElement | null) => { bubbleScrollRefs.current[id] = el; }, []);
  const scrollBubbleToBottom = useCallback((id: string) => {
    const el = bubbleScrollRefs.current[id];
    if (!el) return;
    try { el.scrollTo({ top: el.scrollHeight, behavior: 'auto' }); } catch {}
  }, []);
  
  // Clear Split Chat UI when starting a new chat or switching conversations
  useEffect(() => {
    // Close split panel and clear all bubble state
    setIsSplitOpen(false);
    setBubbleThreads([]);
    setActiveBubbleId(null);
    setSplitStreamingForBubbleId(null);
    setResolvedThreadIdByKey({});
    // Reset scroll refs to avoid stale DOM links
    bubbleScrollRefs.current = {};
    messageRefs.current = {} as any;
  }, [conversationId]);
  const mkThreadTitle = useCallback((text: string) => {
    const t = text || '';
    return `Split from: ${t.slice(0, 40)}${t.length > 40 ? '…' : ''}`;
  }, []);

  // Sync panel state into global debug store (for subcomponents)
  useEffect(() => {
    setDebugStoreOpen(isDebugOpen);
  }, [isDebugOpen, setDebugStoreOpen]);

  // Keyboard shortcut: Cmd/Ctrl + Shift + D to toggle panel
  useEffect(() => {
    if (!canShowDebugToggle) return;
    const handleKeyPress = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        if (isDebugOpen) onDebugClose(); else onDebugOpen();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isDebugOpen, onDebugOpen, onDebugClose, canShowDebugToggle]);

  // Auto-open only once when debugOverride is active
  useEffect(() => {
    if (debugOverride && !isDebugOpen && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      onDebugOpen();
    }
  }, [debugOverride, isDebugOpen, onDebugOpen]);

  // Keep offsets aligned with saved artifact panel size
  useEffect(() => {
    const compute = () => {
      if (!isDebugOpen) {
        setToggleRightOffset('20px');
        setArtifactRightOffset('0px');
        return;
      }
      try {
        const saved = localStorage.getItem('artifact-panel-sizes');
        const sizes = saved ? JSON.parse(saved) : [60, 40];
        const panelPercent = (Array.isArray(sizes) ? sizes[1] : 40);
        setToggleRightOffset(`calc(${panelPercent}% + 20px)`);
        setArtifactRightOffset(`${panelPercent}%`);
      } catch {
        setToggleRightOffset('calc(40% + 20px)');
        setArtifactRightOffset('40%');
      }
    };
    compute();
    const onPanelResize = (e: any) => {
      if (!isDebugOpen) return;
      try {
        const sizes = e?.detail?.sizes || JSON.parse(localStorage.getItem('artifact-panel-sizes') || '[60,40]');
        const panelPercent = Array.isArray(sizes) ? sizes[1] : 40;
        setToggleRightOffset(`calc(${panelPercent}% + 20px)`);
        setArtifactRightOffset(`${panelPercent}%`);
      } catch {}
    };
    window.addEventListener('artifact-panel-resized', onPanelResize as EventListener);
    return () => window.removeEventListener('artifact-panel-resized', onPanelResize as EventListener);
  }, [isDebugOpen]);

  // Split width resize handler (clamped in child)
  const handleSplitResize = useCallback((nextVw: number) => {
    const el = containerRef.current;
    if (!el) return;
    try {
      el.style.setProperty('--split-right-width', `${nextVw}vw`);
    } catch {}
  }, []);
  
  // Initialize conversation from URL (guarded) - ChatEntry handles creation on /chat
  const selectingRef = useRef<string | null>(null);
  const retryCountRef = useRef<number>(0);
  
  useEffect(() => {
    const initConversation = async () => {
      if (isTestMode) return; // Skip conversation selection/creation in test mode
      if (conversationLoading) return;
      
      console.log('[ChatPageV2] initConversation effect running', {
        conversationId,
        conversationLoading,
        hasCurrent: !!currentConversation,
        currentRef: currentConversation?.public_id,
        retryCount: retryCountRef.current,
        timestamp: Date.now()
      });
      
      if (conversationId) {
        // Avoid refetch/select loop if the same conversation is already selected
        if (currentConversation?.public_id === conversationId) {
          console.log('[ChatPageV2] Conversation already selected, skipping');
          retryCountRef.current = 0; // Reset retry count on success
          return;
        }
        if (selectingRef.current === conversationId) {
          console.log('[ChatPageV2] Selection already in progress, skipping');
          return;
        }
        selectingRef.current = conversationId;
        
        try {
          console.log('[ChatPageV2] Selecting conversation:', conversationId);
          
          // Add a small delay if this is a retry to allow state to settle
          if (retryCountRef.current > 0) {
            console.log('[ChatPageV2] Retry attempt', retryCountRef.current, '- adding delay');
            await new Promise(resolve => setTimeout(resolve, 300));
          }
          
          await selectConversation(conversationId);
          console.log('[ChatPageV2] Successfully selected conversation:', conversationId);
          retryCountRef.current = 0; // Reset retry count on success
          
        } catch (error: any) {
          console.error('[ChatPageV2] Failed to load conversation:', error);
          
          // Implement retry logic with exponential backoff
          if (retryCountRef.current < 3) {
            retryCountRef.current++;
            const retryDelay = Math.min(500 * Math.pow(2, retryCountRef.current - 1), 2000);
            console.log(`[ChatPageV2] Will retry in ${retryDelay}ms (attempt ${retryCountRef.current}/3)`);
            
            setTimeout(() => {
              selectingRef.current = null; // Reset to allow retry
              // Trigger re-render to retry
              initConversation();
            }, retryDelay);
          } else {
            console.error('[ChatPageV2] Max retries reached, giving up');
            retryCountRef.current = 0;
            // Only navigate away if we're not already on /chat
            if (window.location.pathname !== '/chat') {
              navigate('/chat', { replace: true });
            }
            clearCurrentConversation();
          }
        } finally {
          if (selectingRef.current === conversationId && retryCountRef.current === 0) {
            selectingRef.current = null;
          }
        }
      } else if (!conversationId) {
        // No id: ChatEntry should handle creation. Render a lightweight loader.
        retryCountRef.current = 0; // Reset retry count when no conversation
        return;
      }
    };
    void initConversation();
  }, [conversationId, conversationLoading, navigate, selectConversation, createConversation, clearCurrentConversation, isTestMode]); // Removed currentConversation?.public_id to prevent circular updates; guard test mode

  // Scroll on messages change
  useEffect(() => {
    if (!viewportRef.current) return;
    const element = viewportRef.current;
    const now = performance.now();
    if (now - lastScrollTsRef.current < 50) return;
    lastScrollTsRef.current = now;
    element.scrollTo({ top: element.scrollHeight, behavior: 'auto' });
    try {
      console.debug('[ChatPageV2] scrolled to bottom on messages change', { count: messagesCount });
    } catch {}
  }, [messagesCount]);

  // Main composer: allow typing always; block send during streaming and short finalize window
  const canType = true;
  const [mainFinalizePending, setMainFinalizePending] = useState<boolean>(false);
  const anySideFinalizePending = useMemo(() => bubbleThreads.some(b => b.pending), [bubbleThreads]);
  const finalizeTimerRef = useRef<number | null>(null);
  useEffect(() => {
    // Clear any timers when streaming starts or on unmount
    if (isStreaming) {
      setMainFinalizePending(false);
      if (finalizeTimerRef.current) { try { clearTimeout(finalizeTimerRef.current); } catch {}; finalizeTimerRef.current = null; }
      return;
    }
    // When a main-lane stream just finished, hold send for a brief finalize window
    if (!isStreaming && !currentStreamIsSide) {
      // Optimistic quick check: if the last assistant has a server UUID, skip hold
      const lastAssistant = (() => {
        for (let i = messages.length - 1; i >= 0; i--) {
          const m: any = messages[i];
          if (m?.role === 'assistant' && !m?.isDraft) return m;
        }
        return null as any;
      })();
      const hasUUID = isUUIDLike(String(lastAssistant?.id || ''));
      if (hasUUID) {
        setMainFinalizePending(false);
        return;
      }
      setMainFinalizePending(true);
      // Wait up to ~1.5s for finalize to persist; then release regardless to avoid blocking UX
      let attempts = 0;
      const tryRelease = () => {
        attempts += 1;
        const la = (() => {
          for (let i = messages.length - 1; i >= 0; i--) {
            const m: any = messages[i];
            if (m?.role === 'assistant' && !m?.isDraft) return m;
          }
          return null as any;
        })();
        if (isUUIDLike(String(la?.id || '')) || attempts >= 6) {
          setMainFinalizePending(false);
          finalizeTimerRef.current = null;
          return;
        }
        finalizeTimerRef.current = window.setTimeout(tryRelease, 250);
      };
      finalizeTimerRef.current = window.setTimeout(tryRelease, 250);
      return () => { if (finalizeTimerRef.current) { try { clearTimeout(finalizeTimerRef.current); } catch {}; finalizeTimerRef.current = null; } };
    }
  }, [isStreaming, currentStreamIsSide, messages]);

  const mainSendLocked = Boolean(isStreaming || mainFinalizePending || anySideFinalizePending);
  const mainPlaceholder = useMemo(() => {
    if (isStreaming) return 'Streaming in progress… you can type; send after it finishes';
    if (mainFinalizePending) return 'Saving previous answer… you can type; send after it finishes';
    if (anySideFinalizePending) return 'Saving split reply… you can type; send after it finishes';
    return 'Ask me anything...';
  }, [isStreaming, mainFinalizePending, anySideFinalizePending]);

  const handleSubmit = useCallback(async (message: string) => {
    console.log('[ChatPage] 📤 HANDLE SUBMIT CALLED:', {
      messageLength: message.length,
      messagePreview: message.substring(0, 100),
      isSending,
      isSubmitting: isSubmittingRef.current,
      timestamp: new Date().toISOString()
    });

    if (!message.trim()) {
      console.log('[ChatPage] ⚠️ Empty message, ignoring');
      return;
    }
    if (isSubmittingRef.current || isStreaming) {
      console.log('[ChatPage] ⚠️ Already submitting/sending, ignoring');
      return;
    }
    isSubmittingRef.current = true;

    try {
      // In test mode, do not create/navigate; use stable test conversation ref
      if (isTestMode) {
        const overrideRef = convRef;
        console.log('[ChatPage] 🧪 Test mode send:', { overrideRef, len: message.length });
        sendMessageToAPI(message, overrideRef);
        return;
      }
      // If on base /chat (no :conversationId), force a new conversation even
      // if a previous currentConversation exists in context.
      const onBaseChat = !conversationId;
      let conversationToUse = onBaseChat ? undefined : currentConversation;
      console.log('[ChatPage] 💬 Current conversation:', {
        hasConversation: !!conversationToUse,
        conversationId: conversationToUse?.conversation_id,
        publicId: conversationToUse?.public_id
      });

      if (!conversationToUse) {
        console.log('[ChatPage] 🆕 Creating new conversation...');
        // Create a new conversation using the old system for now
        conversationToUse = await createConversation();
        if (!conversationToUse) {
          console.error('[ChatPage] ❌ Failed to create conversation');
          return;
        }
        console.log('[ChatPage] ✅ New conversation created:', {
          conversationId: conversationToUse.conversation_id,
          publicId: conversationToUse.public_id
        });
        const newRef = conversationToUse.public_id || conversationToUse.conversation_id;
        // Navigate in parallel; streaming does not depend on route state
        startTransition(() => navigate(`/chat/${newRef}`));
      }

      // Use the fixed Zustand chat to send message
      // Pass the conversation ref directly (prefer public_id, fall back to numeric id)
      const overrideRef = conversationToUse.public_id || String(conversationToUse.conversation_id);
      console.log('[ChatPage] 🚀 Sending message via API:', {
        overrideRef,
        messageLength: message.length,
        timestamp: new Date().toISOString()
      });
      sendMessageToAPI(message, overrideRef);
      

      // Title generation if placeholder
      if (conversationToUse && (!conversationToUse.title || conversationToUse.title === 'New Chat')) {
        const title = message.length > 50 ? `${message.substring(0, 47)}...` : message;
        await updateConversation(conversationToUse.conversation_id, { title });
      }
    } finally {
      setTimeout(() => {
        isSubmittingRef.current = false;
      }, 800);
    }
  }, [currentConversation, createConversation, navigate, sendMessageToAPI, updateConversation, isSending, isTestMode, convRef]);

  // Document upload handlers
  const {
    uploadQueue,
    startUpload,
    cancelUpload,
    retryUpload,
    removeFromQueue,
  } = useDocumentUpload({});

  // Prevent render during initial mount to avoid suspense issues
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
    // Also mark context as ready after a brief delay
    const contextTimer = setTimeout(() => {
      setIsContextReady(true);
    }, 10);
    return () => clearTimeout(contextTimer);
  }, []);

  // Inline edit state for branching conversations
  const isBranchingConv = Boolean(currentConversation?.main_thread_id);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState<string>('');
  const [liked, setLiked] = useState<Set<string>>(new Set());
  const [disliked, setDisliked] = useState<Set<string>>(new Set());
  const [copiedKeys, setCopiedKeys] = useState<Set<string>>(new Set());
  const [splitClickedKeys, setSplitClickedKeys] = useState<Set<string>>(new Set());
  const [hoverCopyKeys, setHoverCopyKeys] = useState<Set<string>>(new Set());
  const [hoverSplitKeys, setHoverSplitKeys] = useState<Set<string>>(new Set());

  const beginEdit = useCallback((msgId: string, currentText: string) => {
    if (!isBranchingConv) return;
    setEditingMessageId(msgId);
    setEditingText(currentText || '');
  }, [isBranchingConv]);

  const cancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setEditingText('');
  }, []);

  const saveEdit = useCallback(async () => {
    if (!isBranchingConv || !editingMessageId || !currentConversation?.main_thread_id) return;
    try {
      // Persist edit to branching API; backend enforces visible-path rule
      await threadService.editMessage(editingMessageId, { content: { text: editingText } });
      // Refresh view from ThreadView and hydrate store deterministically
      const view = await threadService.getThreadView(currentConversation.main_thread_id);
      const convRefResolved = currentConversation.public_id || String(currentConversation.conversation_id);
      const now = Date.now();
      const mapped = (view?.messages || []).map((m: any, idx: number) => ({
        id: m.id,
        clientId: m.id,
        conversationRef: convRefResolved,
        role: (m.role === 'tool' ? 'system' : (m.role as any)),
        content: (m?.content && (m as any).content?.text) || JSON.stringify(m?.content || {}),
        createdAt: new Date(now + idx).toISOString(),
        status: 'saved' as const,
      }));
      useChatStore.getState().upsertMessages(convRefResolved, mapped);
    } catch (e) {
      console.warn('[ChatPage] Edit failed:', e);
    } finally {
      setEditingMessageId(null);
      setEditingText('');
    }
  }, [isBranchingConv, editingMessageId, editingText, currentConversation]);

  // Split: open/focus split bubble linked to a specific assistant message
  const handleSplit = useCallback((anchorMessageId: string) => {
    if (process.env.NODE_ENV !== 'production') {
      try { console.log('[Split] handleSplit called', { anchorMessageId, isUUID: isUUIDLike(anchorMessageId) }); } catch {}
    }
    const key = anchorMessageId;
    setSplitClickedKeys(prev => { const s = new Set(prev); s.add(key); return s; });
    setTimeout(() => {
      setSplitClickedKeys(prev => { const s = new Set(prev); s.delete(key); return s; });
    }, 200);
    setIsSplitOpen(true);
    // Navigation rule: if a thread already exists for this message, focus it
    const existing = bubbleThreads.find(t => t.fromMessageId === anchorMessageId);
    if (existing) {
      setBubbleThreads(prev => prev.map(t => ({ ...t, isOpen: t.id === existing.id })));
      setActiveBubbleId(existing.id);
      scrollToMainMessage(anchorMessageId);
      setTimeout(() => scrollBubbleToBottom(existing.id), 0);
      return;
    }
    // Create new bubble thread (and create real server-side subchat when possible)
    const anchor = messages.find((m: any) => (m.id || m.clientId) === anchorMessageId);
    const title = mkThreadTitle(String(anchor?.content || ''));
    const newId = `split-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const newThread: BubbleThread = {
      id: newId,
      fromMessageId: anchorMessageId,
      title,
      messages: [
        { id: `${newId}-m1`, role: 'assistant', content: String(anchor?.content || '…') },
      ],
      isOpen: true,
      pending: false,
      lastDraftSnapshot: '',
    };
    setBubbleThreads(prev => {
      const next = prev.map(t => ({ ...t, isOpen: false })).concat(newThread);
      persistSplitState(convRef as any, next, newId);
      return next;
    });
    setActiveBubbleId(newId);
    scrollToMainMessage(anchorMessageId);
    setTimeout(() => scrollBubbleToBottom(newId), 0);
    if (process.env.NODE_ENV !== 'production') {
      try { console.log('[Split] Created bubble thread (UI)', { newId, anchorMessageId }); } catch {}
    }

    // Best-effort: resolve anchor to a server thread message id and create subchat thread
    (async () => {
      try {
        // Prefer the already-known convRef (route/public_id) to avoid blocking on context hydration
        const convRefResolved = (convRef || currentConversation?.public_id || currentConversation?.conversation_id || '') as string;
        if (!convRefResolved) return;
    const tryResolveAndCreate = async (attempt: number): Promise<void> => {
          // Resolve to server message id if possible
          let serverAnchorId: string | undefined = anchorMessageId || undefined;
          if (!serverAnchorId && currentConversation?.main_thread_id) {
            try {
              const view = await threadService.getThreadView(currentConversation.main_thread_id);
              const targetTextNorm = normalizeText(String(anchor?.content || ''));
              // Prefer newest matching assistant message
              const msgs = Array.isArray(view?.messages) ? view.messages : [];
              for (let i = msgs.length - 1; i >= 0; i--) {
                const vm: any = msgs[i];
                if (vm?.role !== 'assistant') continue;
                const vmTextNorm = normalizeText((vm?.content && (vm as any).content?.text) || '');
                if (!vmTextNorm || !targetTextNorm) continue;
                if (vmTextNorm === targetTextNorm || vmTextNorm.includes(targetTextNorm) || targetTextNorm.includes(vmTextNorm)) {
                  serverAnchorId = String(vm.id);
                  break;
                }
              }
            } catch (e) {
              // Resolution best-effort
            }
          }

          if (serverAnchorId) {
            setBubbleThreads(prev => { const next = prev.map(t => t.id === newId ? { ...t, anchorServerId: serverAnchorId } : t); persistSplitState(convRef as any, next, newId); return next; });
            try {
              const created = await threadService.startSubchat(convRefResolved, serverAnchorId);
              setBubbleThreads(prev => { const next = prev.map(t => t.id === newId ? { ...t, realThreadId: created.id, creationError: undefined } : t); persistSplitState(convRef as any, next, newId); return next; });
              if (process.env.NODE_ENV !== 'production') {
                try { console.log('[Split] Created real subchat thread', { threadId: created.id, conversationRef: convRefResolved, anchorServerId: serverAnchorId }); } catch {}
              }
            } catch (e) {
              const msg = (e as any)?.message || 'Failed to create split thread';
              console.warn('[Split] Failed to create subchat thread; split may remain UI-only', msg);
              setBubbleThreads(prev => { const next = prev.map(t => t.id === newId ? { ...t, creationError: String(msg) } : t); persistSplitState(convRef as any, next, newId); return next; });
            }
            return;
          }

          // Retry a couple of times to give backend finalize/hydrate time
          if (attempt < 2) {
            const delay = 300 * (attempt + 1);
            setTimeout(() => { void tryResolveAndCreate(attempt + 1); }, delay);
            if (process.env.NODE_ENV !== 'production') {
              try { console.warn('[Split] Anchor not resolved yet, retrying...', { attempt: attempt + 1 }); } catch {}
            }
          } else {
            if (process.env.NODE_ENV !== 'production') {
              try { console.warn('[Split] Could not resolve anchor to server id; subchat will not be created yet'); } catch {}
            }
          }
        };

        await tryResolveAndCreate(0);
      } catch (e) {
        console.warn('[Split] Failed to create subchat thread; split will remain UI-only this session', e);
      }
    })();
  }, [bubbleThreads, messages, mkThreadTitle, scrollToMainMessage, scrollBubbleToBottom]);

  // UI-only split fallback removed: side sends require a real threadId

  const activeBubble = useMemo(() => {
    if (!activeBubbleId) return null as BubbleThread | null;
    return bubbleThreads.find(b => b.id === activeBubbleId) || null;
  }, [activeBubbleId, bubbleThreads]);

  const activeHasRealThread = Boolean(activeBubble?.realThreadId);

  // Side composer behavior change: allow typing during streaming, block only send.
  // Typing is allowed when a bubble is open and not pending finalize.
  const composerTypingEnabled = useMemo(() => {
    if (!activeBubble) return false;
    if (!activeBubble.isOpen) return false;
    // Still block typing while finalize is pending to avoid confusion
    if (activeBubble.pending) return false;
    return true;
  }, [activeBubble]);

  // Disable send when streaming, when finalize is pending, or when no real thread exists yet.
  const sideSendLocked = useMemo(() => {
    const pending = Boolean(activeBubble?.pending);
    return Boolean(isStreaming || pending || !activeHasRealThread || mainFinalizePending);
  }, [isStreaming, activeBubble, activeHasRealThread, mainFinalizePending]);

  // Human-readable reason the split composer is disabled (for placeholder)
  // Placeholder hint: reflect current state even when typing is allowed
  const splitPlaceholder = useMemo(() => {
    if (!activeBubble || !activeBubble.isOpen) return 'Select a split chat to reply';
    if (!activeHasRealThread) return 'Creating split thread…';
    if (isStreaming) return 'Streaming in progress… you can type; send after it finishes';
    if (mainFinalizePending) return 'Saving previous answer… you can type; send after it finishes';
    if (activeBubble.pending) return 'Finishing previous reply…';
    return 'Ask follow-up…';
  }, [activeBubble, activeHasRealThread, isStreaming, mainFinalizePending]);

  const handleSplitComposerSubmit = useCallback((val: string) => {
    const text = (val || '').trim();
    if (!text) return;
    if (!activeBubbleId) return;
    // Prevent side sends from interrupting an in-flight stream (main or side)
    // to keep mapping of pendingUserText -> the active side run unambiguous
    const active = bubbleThreads.find(b => b.id === activeBubbleId);
    if (isStreaming || (active && active.pending)) {
      try {
        toast({
          status: 'info',
          title: 'Finishing previous reply',
          description: 'Please wait for the current response to finish saving before sending another.',
          duration: 2500,
          isClosable: true,
        });
      } catch {}
      if (process.env.NODE_ENV !== 'production') {
        try { console.warn('[Split] Blocking side send: stream in progress or pending finalize'); } catch {}
      }
      return;
    }
    setBubbleThreads(prev => prev.map(x => (
      x.id === activeBubbleId
        ? { ...x, messages: x.messages.concat({ id: `${x.id}-u-${Date.now()}` , role: 'user', content: text }), pending: true }
        : x
    )));
    if (process.env.NODE_ENV !== 'production') {
      try { console.log('[Split] pending=true set for bubble', { bubbleId: activeBubbleId }); } catch {}
    }
    setTimeout(() => scrollBubbleToBottom(activeBubbleId), 0);

    // Stream via unified provider. Prefer targeted thread when available; otherwise fallback to linear.
    try {
      const bubble = bubbleThreads.find(b => b.id === activeBubbleId);
      // Prefer the already-known convRef (route/public_id) to avoid blocking on context hydration
      const convRefResolved = (convRef || currentConversation?.public_id || currentConversation?.conversation_id || '') as string;
      const anchorId = bubble?.fromMessageId;
      // Ensure we have a real thread; attempt to create if missing and we have a UUID-like anchor
      (async () => {
        let threadId: string | undefined = bubble?.realThreadId;
        // Try to create using resolved anchorServerId first
        if (!threadId && convRefResolved) {
          let candidateAnchorId = bubble?.anchorServerId;
          if (!candidateAnchorId && anchorId) {
            candidateAnchorId = anchorId;
          }
          if (!candidateAnchorId && currentConversation?.main_thread_id) {
            // Best-effort resolution by content match
            try {
              const view = await threadService.getThreadView(currentConversation.main_thread_id);
              const anchor = messages.find((m: any) => (m.id || m.clientId) === anchorId);
              const targetTextNorm = normalizeText(String(anchor?.content || ''));
              const msgs = Array.isArray(view?.messages) ? view.messages : [];
              for (let i = msgs.length - 1; i >= 0; i--) {
                const vm: any = msgs[i];
                if (vm?.role !== 'assistant') continue;
                const vmTextNorm = normalizeText((vm?.content && (vm as any).content?.text) || '');
                if (!vmTextNorm || !targetTextNorm) continue;
                // Robust match: exact normalized OR contains-either-way for minor diffs
                if (vmTextNorm === targetTextNorm || vmTextNorm.includes(targetTextNorm) || targetTextNorm.includes(vmTextNorm)) {
                  candidateAnchorId = String(vm.id);
                  break;
                }
              }
            } catch {}
          }
          if (candidateAnchorId) {
            try {
              const created = await threadService.startSubchat(convRefResolved, candidateAnchorId);
              threadId = created.id;
              setBubbleThreads(prev => prev.map(t => t.id === bubble?.id ? { ...t, realThreadId: created.id, anchorServerId: candidateAnchorId } : t));
            } catch (e) {
              console.warn('[Split] Failed to create subchat for streaming; falling back to UI-only', e);
            }
          }
        }
        if (convRefResolved) {
          // Enforce threaded-only split sends: require a real threadId
          if (!threadId) {
            if (process.env.NODE_ENV !== 'production') {
              try { console.warn('[Split] Aborting side send: no threadId (subchat not created)'); } catch {}
            }
            try {
              toast({
                status: 'info',
                title: 'Cannot split unsaved messages',
                description: 'Select a saved assistant message to split; can’t split unsaved messages.',
                duration: 4000,
                isClosable: true,
              });
            } catch {}
            return;
          }
          if (process.env.NODE_ENV !== 'production') {
            try { console.log('[Split] Streaming send', { conversationRef: convRefResolved, threadId: threadId || null, anchorId }); } catch {}
          }
          // Use the standard chat flow; include thread target and anchor parent when available.
          // Mark as side stream so the main lane doesn't show duplicates.
          const parentMessageId = (bubble?.anchorServerId || candidateAnchorId || undefined) as string | undefined;
          sendMessageToAPI(text, convRefResolved, { threadId, parentMessageId, sideStream: true });
          // Mark this bubble as the streaming target so we can render tokens inside it
          if (activeBubbleId) setSplitStreamingForBubbleId(activeBubbleId);
        }
      })();
    } catch {}
  }, [activeBubbleId, scrollBubbleToBottom]);

  // Clear side streaming marker handled after hydration success or timeout

  // Read current streaming draft content to mirror inside the side bubble
  const draftClientId = useChatStore((s) => s.currentStream?.draftClientId);
  const draftContent = useChatStore((s) => (draftClientId ? (s.messagesById[draftClientId]?.content || '') : ''));
  const currentPendingUserText = useChatStore((s) => s.currentStream?.pendingUserText || '');
  const pendingUserTextRef = useRef<string>('');
  useEffect(() => {
    if (currentPendingUserText) pendingUserTextRef.current = currentPendingUserText;
  }, [currentPendingUserText]);

  // Keep a snapshot of the latest streamed draft per active bubble so we can
  // render it even after targeting clears at stream end.
  useEffect(() => {
    if (!splitStreamingForBubbleId) return;
    if (!draftContent) return;
    setBubbleThreads(prev => prev.map(b => (
      b.id === splitStreamingForBubbleId
        ? { ...b, lastDraftSnapshot: String(draftContent) }
        : b
    )));
  }, [draftContent, splitStreamingForBubbleId]);

  // Hydrate bubble content from ThreadView after a side stream completes
  useEffect(() => {
    const tid = currentStreamThreadId;
    if (!isStreaming && currentStreamIsSide && tid) {
      let cancelled = false;
      const maxAttempts = 8; // ~2s total with 250ms steps
      const tryHydrate = async (attempt: number) => {
        if (cancelled) return;
        try {
          if (process.env.NODE_ENV !== 'production') {
            try { console.log('[Split] hydrate attempt', { attempt, tid, ts: Date.now() }); } catch {}
          }
          const view = await threadService.getThreadView(tid);
          const msgs = Array.isArray(view?.messages) ? view.messages : [];
          // Robust detection: find latest user matching our pending text, then first assistant after
          const pendingTextRaw = pendingUserTextRef.current || '';
          const pendingTextNorm = normalizeText(String(pendingTextRaw));
          let userIdx = -1;
          if (pendingTextNorm) {
            for (let i = msgs.length - 1; i >= 0; i--) {
              const m: any = msgs[i];
              if (m?.role !== 'user') continue;
              const txt = (m?.content && (m as any).content?.text) || JSON.stringify(m?.content || {});
              if (normalizeText(String(txt)) === pendingTextNorm) { userIdx = i; break; }
            }
          }
          if (userIdx !== -1) {
            let nextAssistant: any | null = null;
            for (let i = userIdx + 1; i < msgs.length; i++) {
              const m: any = msgs[i];
              if (m?.role === 'assistant') { nextAssistant = m; break; }
            }
            const content = nextAssistant
              ? ((nextAssistant?.content && (nextAssistant as any).content?.text) || JSON.stringify(nextAssistant?.content || {}))
              : '';
            if (content && !cancelled) {
              let targetBubbleId: string | null = null;
              setBubbleThreads(prev => {
                let updated: BubbleThread[] = prev.map(b => {
                  if (b.realThreadId !== tid) return b;
                  targetBubbleId = b.id;
                  const already = b.messages.some(mm => mm.role === 'assistant' && (mm.content || '').trim() === (content || '').trim());
                  const next = already ? b : { ...b, messages: b.messages.concat({ id: `${b.id}-asst-${Date.now()}`, role: 'assistant', content }) };
                  return { ...next, pending: false, lastDraftSnapshot: '' };
                });
                // Persist state
                try { persistSplitState(convRef as any, updated, activeBubbleId); } catch {}
                return updated;
              });
              // Clear the streaming mirror once we have persisted content
              if (splitStreamingForBubbleId) {
                if (process.env.NODE_ENV !== 'production') {
                  try { console.log('[Split] hydrate success; clearing mirror', { bubbleId: splitStreamingForBubbleId }); } catch {}
                }
                setSplitStreamingForBubbleId(null);
              }
              // Ensure the appended assistant is fully visible
              if (targetBubbleId) setTimeout(() => scrollBubbleToBottom(targetBubbleId!), 0);
              return; // success
            }
          } else {
            if (process.env.NODE_ENV !== 'production') {
              try { console.log('[Split] hydrate: pending user not visible yet; will retry'); } catch {}
            }
          }
        } catch (e) {
          try { console.warn('[Split] Hydrate bubble after complete attempt failed:', e); } catch {}
        }
        if (attempt < maxAttempts && !cancelled) {
          setTimeout(() => { void tryHydrate(attempt + 1); }, 250);
        } else {
          // Give up: append a fallback assistant using the final streamed draft
          try {
            const fallback = (draftContent || '').trim();
            if (fallback) {
              let targetBubbleId: string | null = null;
              setBubbleThreads(prev => {
                let updated: BubbleThread[] = prev.map(b => {
                  if (b.realThreadId !== tid) return b;
                  targetBubbleId = b.id;
                  const already = b.messages.some(mm => mm.role === 'assistant' && (mm.content || '').trim() === fallback);
                  const next = already ? b : { ...b, messages: b.messages.concat({ id: `${b.id}-asst-${Date.now()}`, role: 'assistant', content: fallback }) };
                  return { ...next, pending: false, lastDraftSnapshot: '' };
                });
                try { persistSplitState(convRef as any, updated, activeBubbleId); } catch {}
                return updated;
              });
              if (process.env.NODE_ENV !== 'production') {
                try { console.log('[Split] fallback appended from zustand draft'); } catch {}
              }
              if (targetBubbleId) setTimeout(() => scrollBubbleToBottom(targetBubbleId!), 0);
            }
          } catch {}
          // Clear the streaming mirror even if we couldn't hydrate
          if (splitStreamingForBubbleId) {
            if (process.env.NODE_ENV !== 'production') {
              try { console.log('[Split] hydrate complete (fallback or timeout); clearing mirror', { bubbleId: splitStreamingForBubbleId }); } catch {}
            }
            setSplitStreamingForBubbleId(null);
          }
        }
      };
      void tryHydrate(0);
      return () => { cancelled = true; };
    }
  }, [isStreaming, currentStreamIsSide, currentStreamThreadId, splitStreamingForBubbleId, draftContent]);

  // Keep the active bubble scrolled to the bottom as tokens stream so the
  // inline assistant draft grows naturally within the message area.
  useEffect(() => {
    if (isStreaming && splitStreamingForBubbleId) {
      // Defer scroll until after layout/paint to prevent momentary jump
      let raf1 = 0, raf2 = 0;
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
          const id = splitStreamingForBubbleId!;
          scrollBubbleToBottom(id);
          const el = bubbleScrollRefs.current[id];
          if (el) {
            try {
              const anchor = el.querySelector('.scroll-anchor') as HTMLElement | null;
              if (anchor && anchor.scrollIntoView) anchor.scrollIntoView({ block: 'end' });
            } catch {}
          }
        });
      });
      return () => { try { cancelAnimationFrame(raf1); } catch {}; try { cancelAnimationFrame(raf2); } catch {}; };
    }
  }, [draftContent, isStreaming, splitStreamingForBubbleId, scrollBubbleToBottom]);

  // Combine debug artifact offset with split view width so fixed UI elements don't overlap
  const rightOffsetCombined = useMemo(() => artifactRightOffset, [artifactRightOffset]);
  const toggleRightCombined = useMemo(() => toggleRightOffset, [toggleRightOffset]);

  // Load split state on conversation mount
  useEffect(() => {
    if (!convRef) return;
    loadSplitState(convRef as any);
  }, [convRef, loadSplitState]);

  // Persist on state changes (skip while hydrating)
  useEffect(() => {
    if (!convRef || bubblesHydratingRef.current) return;
    persistSplitState(convRef as any, bubbleThreads, activeBubbleId);
  }, [bubbleThreads, activeBubbleId, convRef, persistSplitState]);

  const handleCopy = useCallback(async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text || '');
      // flash copy button blue briefly; no toast per request
      setCopiedKeys(prev => { const s = new Set(prev); s.add(key); return s; });
      setTimeout(() => {
        setCopiedKeys(prev => { const s = new Set(prev); s.delete(key); return s; });
      }, 2000);
    } catch (e) {
      // no toast notifications for copy per request
      console.warn('[ChatPage] Copy failed:', e);
    }
  }, []);

  const handleFeedback = useCallback((messageId: string, kind: 'good' | 'bad') => {
    const alreadyGood = liked.has(messageId);
    const alreadyBad = disliked.has(messageId);

    if (kind === 'good') {
      if (alreadyGood) {
        // Unselect good
        setLiked(prev => { const s = new Set(prev); s.delete(messageId); return s; });
      } else {
        // Select good and clear bad
        setLiked(prev => { const s = new Set(prev); s.add(messageId); return s; });
        setDisliked(prev => { const s = new Set(prev); s.delete(messageId); return s; });
      }
    } else {
      if (alreadyBad) {
        // Unselect bad
        setDisliked(prev => { const s = new Set(prev); s.delete(messageId); return s; });
      } else {
        // Select bad and clear good
        setDisliked(prev => { const s = new Set(prev); s.add(messageId); return s; });
        setLiked(prev => { const s = new Set(prev); s.delete(messageId); return s; });
      }
    }
    // No toasts for feedback toggles to avoid noise
    // TODO: send to backend feedback endpoint when available
  }, [liked, disliked]);

  // Dev-only: seed mock messages on /chat-test for visual testing when store is empty
  const seededRef = useRef(false);
  useEffect(() => {
    if (!isTestMode) return;
    if (!enableTestSeed) return; // respect flag to disable seeding
    if (seededRef.current) return;
    if (!convRef) return;
    if (messages.length > 0) return;
    seededRef.current = true;
    const now = Date.now();
    const seed = [
      {
        clientId: `user-seed-${now}`,
        conversationRef: convRef,
        role: 'user' as const,
        content: 'Explain the Pythagorean theorem with a simple example.',
        createdAt: new Date(now - 1000).toISOString(),
        status: 'saved' as const,
        isDraft: false,
      },
      {
        clientId: `asst-seed-${now}`,
        conversationRef: convRef,
        role: 'assistant' as const,
        content: 'The Pythagorean theorem states that in a right triangle, a^2 + b^2 = c^2. For a 3-4-5 triangle: 3^2 + 4^2 = 9 + 16 = 25 = 5^2.',
        createdAt: new Date(now - 500).toISOString(),
        status: 'saved' as const,
        isDraft: false,
      },
    ];
    useChatStore.getState().upsertMessages(convRef, seed as any);
    try { console.log('[ChatPage] Seeded mock messages for /chat-test'); } catch {}
  }, [isTestMode, convRef, messagesCount, enableTestSeed]);

  
  // Do not block the whole page; compute a lightweight inline hydration hint instead
  // Suppress when streaming or actively submitting to avoid transient flicker post-create
  const showInlineHydration = (
    !isTestMode &&
    !!conversationId &&
    !currentConversation &&
    !isStreaming &&
    !isSubmittingRef.current
  );

  const hasProcessingDocuments = uploadQueue.some(
    (item) => item.status === 'completed' && item.processingStatus && ['pending', 'queued', 'processing'].includes(item.processingStatus)
  );

  return (
    <ChatLayout>
      <ArtifactLayout
        isArtifactOpen={canShowDebugToggle ? isDebugOpen : false}
        onArtifactClose={onDebugClose}
        artifactContent={canShowDebugToggle ? <ArtifactDebugPanel onClose={onDebugClose} /> : null}
        defaultSizes={[60, 40]}
        minSizes={[40, 30]}
      >
        {!simpleMode && <ConnectionStatusIndicator position="top" className="chat-conn-status" />}
        {/* Stream request id badge for quick triage (hidden when no active/last stream) */}
        {!simpleMode && <StreamRequestIdBadge />}

        {/* Inline, non-blocking hydration hint (no full-page loader) */}
        {showInlineHydration && (
          <div style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '8px 0',
            color: 'var(--color-text-muted)'
          }}>
            Loading conversation…
          </div>
        )}
        
        {/* Debug Panel Toggle */}
        {canShowDebugToggle && (
          <Tooltip label={isDebugOpen ? 'Close Debug Panel (Esc)' : 'Open Personalization Debug Panel (Cmd+Shift+D)'} placement="left">
            <IconButton
              aria-label={isDebugOpen ? 'Close debug panel' : 'Open debug panel'}
              icon={<FiActivity />}
              position="fixed"
              top="80px"
              right={toggleRightCombined}
              zIndex={250}
              onClick={isDebugOpen ? onDebugClose : onDebugOpen}
              colorScheme="purple"
              variant={isDebugOpen ? 'solid' : 'outline'}
              size="md"
              shadow="lg"
            />
          </Tooltip>
        )}

        {/* Split toggle removed; use assistant message action button */}

        <div
          className={`chat-container ${isSplitOpen ? 'split-open' : ''}`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            position: 'relative',
            overflow: 'visible',
            // Ensure scroll areas know the composer height to avoid overlap
            ['--composer-min-height' as any]: '48px',
            ['--artifact-right-offset' as any]: rightOffsetCombined,
            // Split variables
            ['--split-right-width' as any]: isSplitOpen ? '40vw' : '0vw',
            ['--fixed-right-offset' as any]: isSplitOpen
              ? `calc(${rightOffsetCombined} + var(--split-right-width) + var(--split-gap))`
              : rightOffsetCombined,
            // Left and right lane widths; cap at unsplit max
            // Content lane widths; capped to max. Gutters are handled via border-box on .lane
            ['--left-lane-width' as any]: isSplitOpen
              ? 'min(calc(100vw - var(--split-right-width) - var(--sidebar-fixed-left)), var(--chat-lane-max))'
              : 'var(--chat-lane-max)',
            ['--right-lane-width' as any]: isSplitOpen
              ? 'min(calc(var(--split-right-width)), var(--chat-lane-max))'
              : '0px',
            // Default legacy lane var points to left lane
            ['--chat-lane-width' as any]: 'var(--left-lane-width)'
          }}
          ref={containerRef}
        >
          {/* Permanent bookmark toggle when collapsed; only show when there are messages */}
          {shouldRenderSplitBookmark && (
            <button
              className="split-bookmark-toggle"
              onClick={() => setIsSplitOpen(true)}
              aria-label={"Open split view"}
              title={"Open split chat"}
            >
              <span className="split-bookmark-icon" aria-hidden>
                <SplitChatIcon size={14} />
              </span>
            </button>
          )}
          {/* Offset wrapper to center content within available space (respects sidebar hover/lock) */}
          <div className="chat-content-offset">
            {/* Empty state headline */}
            {messages.length === 0 && !isStreaming && (
              <div className="empty-state-headline">
                <div className="empty-state-inner">Ready to learn now</div>
              </div>
            )}
            <DocumentUploadZone 
              showAlways={false}
              uploadHandlers={{ uploadQueue, startUpload, cancelUpload, retryUpload, removeFromQueue }}
            />

            {isSplitOpen ? (
              <PanelGroup direction="horizontal" className="split-group">
                <Panel minSize={30} defaultSize={60} className="split-panel">
                  {/* Leaf-style toggle anchored to LEFT side of divider (attached to left panel) */}
                  <button className="split-leaf-toggle-left" onClick={() => setIsSplitOpen(false)} aria-label="Collapse split view">
                    <span className="split-bookmark-icon" aria-hidden>
                      <SplitChatIcon size={14} />
                    </span>
                  </button>
                  <div
                    className="chat-viewport"
                    ref={viewportRef}
                    style={{ flex: 1, overflow: 'auto', overflowX: 'hidden', position: 'relative', height: '100%', maxHeight: 'calc(100vh - 120px)' }}
                  >
                    <div className="lane left-lane">
                      {messages.map((m: any) => {
                        const keyId = m.id || m.clientId;
                        return (
                        <div ref={setMsgRef(String(keyId))} key={keyId} className={`message-container ${m.role === 'user' ? 'user-message-container' : ''}`}>
                          <div className={`message-wrapper ${m.role} message-existing`}>
                            <div className="message-content-area">
                              {m.role === 'user' ? (
                                <div className="user-message-bubble">
                                  <div className={`chatv2-msg ${m.role}`}>
                                    <div className="message-text">
                                      {isBranchingConv && editingMessageId === (m.id || m.clientId) ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                          <textarea value={editingText} onChange={(e) => setEditingText(e.target.value)} rows={3} style={{ width: '100%', resize: 'vertical' }} />
                                          <div style={{ display: 'flex', gap: '8px' }}>
                                            <button onClick={saveEdit} className="btn btn-primary" style={{ padding: '6px 10px' }}>Save</button>
                                            <button onClick={cancelEdit} className="btn" style={{ padding: '6px 10px' }}>Cancel</button>
                                          </div>
                                        </div>
                                      ) : (
                                        <MarkdownRender>{m.content}</MarkdownRender>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className={`chatv2-msg ${m.role}`}>
                                  <div className="message-text">
                                    <MarkdownRender>{m.content}</MarkdownRender>
                                  </div>
                                  {!m.isDraft && (
                                    <div className="message-actions" style={{ marginTop: '6px', textAlign: 'left' }}>
                                      {(() => {
                                        const key = m.id || m.clientId;
                                        // Completion‑gated rule: only allow split for saved assistant messages
                                        // with a server UUID id. Dev fallback can force‑enable via query/env.
                                        const isSavedAssistant = m.role === 'assistant' && !m.isDraft;
                                        // Require a server UUID to avoid ambiguous/non-anchored splits
                                        const hasServerUUID = isUUIDLike(String(m.id || ''));
                                        const canSplit = Boolean(isSavedAssistant && hasServerUUID);
                                        let splitColor = 'var(--color-text-muted)';
                                        if (!canSplit) {
                                          splitColor = 'var(--color-border)';
                                        } else if (splitClickedKeys.has(key)) {
                                          splitColor = 'var(--color-primary)';
                                        } else if (hoverSplitKeys.has(key)) {
                                          splitColor = 'var(--color-primary-light)';
                                        }
                                        const disabledTitle = !isSavedAssistant
                                          ? 'Waiting for the assistant to finish…'
                                          : (!hasServerUUID ? 'Preparing saved answer… try again in a moment.' : 'Split');
                                        return (
                                          <button
                                            className="btn btn-link"
                                            style={{ fontSize: '12px', color: splitColor, marginRight: 10 }}
                                            data-anchor-id={String(m.id || '')}
                                            onClick={(ev) => {
                                              if (!canSplit) return;
                                              const aid = (ev.currentTarget as any)?.dataset?.anchorId || String(m.id || '');
                                              if (aid) handleSplit(String(aid));
                                            }}
                                            onMouseEnter={() => setHoverSplitKeys(prev => { const s = new Set(prev); s.add(key); return s; })}
                                            onMouseLeave={() => setHoverSplitKeys(prev => { const s = new Set(prev); s.delete(key); return s; })}
                                            title={canSplit ? 'Split' : disabledTitle}
                                            disabled={!canSplit}
                                          >
                                            <SplitChatIcon size={14} style={{ marginRight: 4 }} /> Split
                                          </button>
                                        );
                                      })()}
                                      <button className="btn btn-link" style={{ fontSize: '12px', color: copiedKeys.has(m.id || m.clientId) ? 'var(--color-primary)' : 'var(--color-text-muted)', marginRight: 10 }} onClick={() => handleCopy(m.id || m.clientId, m.content || '')}>
                                        <Copy size={14} style={{ marginRight: 4 }} /> {copiedKeys.has(m.id || m.clientId) ? 'Copied' : 'Copy'}
                                      </button>
                                      {(() => {
                                        const key = (m.id || m.clientId) as string;
                                        const resolvedId = resolvedThreadIdByKey[key];
                                        const onResolve = async (): Promise<string | null> => {
                                          if (resolvedThreadIdByKey[key]) return resolvedThreadIdByKey[key];
                                          const mainId = currentConversation?.main_thread_id as string | undefined;
                                          if (!mainId) return null;
                                          const candidateText = normalizeText(String(m.content || ''));
                                          const tryOnce = async () => await resolveThreadMsgId(mainId, candidateText);
                                          let id = await tryOnce();
                                          if (!id) { await new Promise(r => setTimeout(r, 300)); id = await tryOnce(); }
                                          if (!id) { await new Promise(r => setTimeout(r, 600)); id = await tryOnce(); }
                                          if (id) setResolvedThreadIdByKey(prev => ({ ...prev, [key]: id }));
                                          return id;
                                        };
                                        return (
                                          <MessageFeedback
                                            conversationRef={convRef as any}
                                            threadMessageId={String(m.id || '')}
                                            localOnly={isTestMode}
                                            resolvedThreadMessageId={resolvedId}
                                            onResolve={onResolve}
                                          />
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );})}
                      {isStreaming && !currentStreamIsSide && (
                        <div className="typing-indicator-wrapper">
                          <TypingIndicator visible message="Thinking" isError={Boolean(messagesError)} isDisconnected={false} />
                        </div>
                      )}
                      {!isStreaming && (connectionError || Boolean(messagesError)) && (
                        <div className="typing-indicator-wrapper">
                          <TypingIndicator visible message={(messagesError as any)?.message || (connectionError ? 'Request failed. Try shortening your message or retry.' : 'There was an issue fetching messages.')} isError isDisconnected={false} />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="lane left-lane" style={{ paddingBottom: '1rem', position: 'relative', zIndex: 10 }}>
                    <ChatInputBar
                      onSubmit={handleSubmit}
                      placeholder={mainPlaceholder}
                      enableTools
                      disabled={!canType}
                      sendDisabled={mainSendLocked}
                      data-debug-disabled={!canType}
                      data-debug-thread
                      data-debug-connected={!connectionError}
                      isEmptyState={messages.length === 0}
                      isCentered={messages.length === 0}
                      onFullScreenChange={setIsInputFullScreen}
                      containerStyle={{ ['--composer-min-height' as any]: '48px' }}
                    />
                  </div>
                </Panel>
                <PanelResizeHandle className="split-handle" />
                <Panel minSize={30} defaultSize={40} className="split-panel">
                  {/* Removed background band for cleaner, glassy look */}
                  <div className="split-scroll">
                    <div className="lane" style={{ ['--chat-lane-width' as any]: '100%' }}>
                      {/* Floating header for active split chat (title + toggle); always visible */}
                      <div className="split-topbar">
                          <button
                            className="split-topbar-toggle"
                            aria-label="Collapse split chat"
                            onClick={() => {
                              if (!activeBubbleId) return;
                              setBubbleThreads(prev => { const next = prev.map(x => x.id === activeBubbleId ? { ...x, isOpen: false } : x); persistSplitState(convRef as any, next, null); return next; });
                              setActiveBubbleId(null);
                            }}
                          >
                            ▾
                          </button>
                          <div className="split-topbar-title">
                            {bubbleThreads.find(b => b.id === activeBubbleId)?.title || 'Split chat'}
                          </div>
                      </div>
                      <div className={`bubbles-scroll ${activeBubbleId ? 'has-active-bubble' : ''}`}>
                        {(() => {
                          const visible = activeBubbleId
                            ? bubbleThreads.filter(t => t.id === activeBubbleId)
                            : bubbleThreads;
                          if (visible.length === 0) {
                            return (
                              <div style={{ fontSize: 13, color: '#6b7280', padding: '8px 4px' }}>
                                Click “Split” on an AI message to open a side thread.
                              </div>
                            );
                          }
                          return visible.map(t => (
                            <div key={t.id} className={`bubble ${t.isOpen ? 'is-active' : ''}`}>
                              {!t.isOpen && (
                                <div className="bubble-hd" role="button" aria-expanded={t.isOpen}>
                                  <button
                                    className="bubble-toggle"
                                    aria-label={'Expand split chat'}
                                    onClick={() => {
                                      setBubbleThreads(prev => { const next = prev.map(x => ({ ...x, isOpen: x.id === t.id })); persistSplitState(convRef as any, next, t.id); return next; });
                                      setActiveBubbleId(t.id);
                                      scrollToMainMessage(t.fromMessageId);
                                      setTimeout(() => scrollBubbleToBottom(t.id), 0);
                                    }}
                                  >
                                    ▸
                                  </button>
                                  <div className="bubble-hd-main" onClick={() => {
                                    setBubbleThreads(prev => { const next = prev.map(x => ({ ...x, isOpen: x.id === t.id })); persistSplitState(convRef as any, next, t.id); return next; });
                                    setActiveBubbleId(t.id);
                                    scrollToMainMessage(t.fromMessageId);
                                    setTimeout(() => scrollBubbleToBottom(t.id), 0);
                                  }}>
                                  <div className="bubble-title">{t.title}</div>
                                  <div className="bubble-sub">Subchat length: {t.messages.filter(mm => mm.role === 'user').length}</div>
                                </div>
                              </div>
                              )}
                              {t.isOpen && (
                                <div className="bubble-body">
                                  <div className="bubble-thread" ref={setBubbleRef(t.id)}>
                                    {t.messages.map(mm => {
                                      const keyId = mm.id;
                                      return (
                                        <div key={keyId} className={`message-container ${mm.role === 'user' ? 'user-message-container' : ''}`}>
                                          <div className={`message-wrapper ${mm.role} message-existing`}>
                                            <div className="message-content-area">
                                              {mm.role === 'user' ? (
                                                <div className="user-message-bubble">
                                                  <div className={`chatv2-msg ${mm.role}`}>
                                                    <div className="message-text">
                                                      <MarkdownRender>{mm.content}</MarkdownRender>
                                                    </div>
                                                  </div>
                                                </div>
                                              ) : (
                                                <div className={`chatv2-msg ${mm.role}`}>
                                                  <div className="message-text">
                                                    <MarkdownRender>{mm.content}</MarkdownRender>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                    {/* Inline draft while streaming OR while pending finalize (guarded by bubble.pending). */}
                                    {(() => {
                                      const targeted = currentStreamThreadId && t.realThreadId === currentStreamThreadId;
                                      const showWhileStreaming = Boolean(isStreaming && targeted);
                                      const showWhilePending = Boolean(t.pending && !isStreaming);
                                      const shouldShow = showWhileStreaming || showWhilePending;
                                      return shouldShow ? (
                                      <div className={`message-container`}>
                                        <div className={`message-wrapper assistant message-existing`}>
                                          <div className="message-content-area">
                                            <div className={`chatv2-msg assistant`}>
                                              <div className="message-text">
                                                {/* Thinking indicator only while actively streaming to this bubble */}
                                                <SplitThinkingIndicator visible={showWhileStreaming} message={'Thinking'} />
                                                {/* Streamed tokens (live) or pending snapshot only when idle */}
                                                {(() => {
                                                  if (showWhileStreaming) {
                                                    const text = (draftContent || '').trim();
                                                    return text ? (<MarkdownRender>{text}</MarkdownRender>) : null;
                                                  }
                                                  if (showWhilePending) {
                                                    const text = (t.lastDraftSnapshot || '').trim();
                                                    return text ? (<MarkdownRender>{text}</MarkdownRender>) : null;
                                                  }
                                                  return null;
                                                })()}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      ) : null;
                                    })()}
                                    {/* Bottom scroll anchor to stabilize scroll anchoring during streaming */}
                                    <div className="scroll-anchor" aria-hidden="true" />
                                  </div>
                                </div>
                              )}
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  </div>
                  {/* Bottom composer for active split chat; dim when disabled */}
                  <div className="split-composer lane" aria-disabled={!composerTypingEnabled} style={{ ['--chat-lane-width' as any]: '100%', ['--split-dim-opacity' as any]: '0.85' }}>
                    {/* Status note when active bubble lacks a real thread */}
                    {activeBubble && !activeHasRealThread && (
                      <div style={{
                        fontSize: 12,
                        color: (activeBubble.creationError
                          ? 'var(--color-danger, #c00)'
                          : 'var(--color-text-muted)'),
                        margin: '4px 4px 6px',
                      }}>
                        {activeBubble.creationError
                          ? `Failed to create split: ${activeBubble.creationError}`
                          : 'Creating split thread… If this doesn’t change, check network for /api/v1/threads.'}
                      </div>
                    )}
                    <ChatInputBar
                      onSubmit={handleSplitComposerSubmit}
                      placeholder={splitPlaceholder}
                      enableTools={false}
                      disabled={!composerTypingEnabled}
                      sendDisabled={sideSendLocked}
                      containerStyle={{
                        marginTop: 0,
                        ['--composer-min-height' as any]: '48px'
                      }}
                    />
                  </div>
                </Panel>
              </PanelGroup>
            ) : (
              <>
                <div
                  className="chat-viewport"
                  ref={viewportRef}
                  style={{ flex: 1, overflow: 'auto', overflowX: 'hidden', position: 'relative', height: '100%', maxHeight: 'calc(100vh - 120px)' }}
                >
                  <div className="lane left-lane">
                    {messages.map((m: any) => {
                      const keyId = m.id || m.clientId;
                      return (
                      <div ref={setMsgRef(String(keyId))} key={keyId} className={`message-container ${m.role === 'user' ? 'user-message-container' : ''}`}>
                        <div className={`message-wrapper ${m.role} message-existing`}>
                          <div className="message-content-area">
                            {m.role === 'user' ? (
                              <div className="user-message-bubble">
                                <div className={`chatv2-msg ${m.role}`}>
                                  <div className="message-text">
                                    {isBranchingConv && editingMessageId === (m.id || m.clientId) ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <textarea value={editingText} onChange={(e) => setEditingText(e.target.value)} rows={3} style={{ width: '100%', resize: 'vertical' }} />
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                          <button onClick={saveEdit} className="btn btn-primary" style={{ padding: '6px 10px' }}>Save</button>
                                          <button onClick={cancelEdit} className="btn" style={{ padding: '6px 10px' }}>Cancel</button>
                                        </div>
                                      </div>
                                    ) : (
                                      <MarkdownRender>{m.content}</MarkdownRender>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className={`chatv2-msg ${m.role}`}>
                                <div className="message-text">
                                  <MarkdownRender>{m.content}</MarkdownRender>
                                </div>
                                  {!m.isDraft && (
                                    <div className="message-actions" style={{ marginTop: '6px', textAlign: 'left' }}>
                                      {(() => {
                                        const key = m.id || m.clientId;
                                        const isSavedAssistant = m.role === 'assistant' && !m.isDraft;
                                        const hasServerUUID = isUUIDLike(String(m.id || ''));
                                        const canSplit = Boolean(isSavedAssistant && hasServerUUID);
                                        let splitColor = 'var(--color-text-muted)';
                                        if (!canSplit) {
                                          splitColor = 'var(--color-border)';
                                        } else if (splitClickedKeys.has(key)) {
                                          splitColor = 'var(--color-primary)';
                                        } else if (hoverSplitKeys.has(key)) {
                                          splitColor = 'var(--color-primary-light)';
                                        }
                                        const disabledTitle = !isSavedAssistant
                                          ? 'Waiting for the assistant to finish…'
                                          : (!hasServerUUID ? 'Preparing saved answer… try again in a moment.' : 'Split');
                                        return (
                                          <button
                                            className="btn btn-link"
                                            style={{ fontSize: '12px', color: splitColor, marginRight: 10 }}
                                            onClick={() => { if (canSplit && m.id) handleSplit(String(m.id)); }}
                                            onMouseEnter={() => setHoverSplitKeys(prev => { const s = new Set(prev); s.add(key); return s; })}
                                            onMouseLeave={() => setHoverSplitKeys(prev => { const s = new Set(prev); s.delete(key); return s; })}
                                            title={canSplit ? 'Split' : disabledTitle}
                                            disabled={!canSplit}
                                          >
                                            <SplitChatIcon size={14} style={{ marginRight: 4 }} /> Split
                                          </button>
                                        );
                                      })()}
                                      <button className="btn btn-link" style={{ fontSize: '12px', color: copiedKeys.has(m.id || m.clientId) ? 'var(--color-primary)' : 'var(--color-text-muted)', marginRight: 10 }} onClick={() => handleCopy(m.id || m.clientId, m.content || '')}>
                                        <Copy size={14} style={{ marginRight: 4 }} /> {copiedKeys.has(m.id || m.clientId) ? 'Copied' : 'Copy'}
                                      </button>
                                      {(() => {
                                        const key = (m.id || m.clientId) as string;
                                        const resolvedId = resolvedThreadIdByKey[key];
                                        const onResolve = async (): Promise<string | null> => {
                                          if (resolvedThreadIdByKey[key]) return resolvedThreadIdByKey[key];
                                          const mainId = currentConversation?.main_thread_id as string | undefined;
                                          if (!mainId) return null;
                                          const candidateText = normalizeText(String(m.content || ''));
                                          const tryOnce = async () => await resolveThreadMsgId(mainId, candidateText);
                                          let id = await tryOnce();
                                          if (!id) { await new Promise(r => setTimeout(r, 300)); id = await tryOnce(); }
                                          if (!id) { await new Promise(r => setTimeout(r, 600)); id = await tryOnce(); }
                                          if (id) setResolvedThreadIdByKey(prev => ({ ...prev, [key]: id }));
                                          return id;
                                        };
                                        return (
                                          <MessageFeedback
                                            conversationRef={convRef as any}
                                            threadMessageId={String(m.id || '')}
                                            localOnly={isTestMode}
                                            resolvedThreadMessageId={resolvedId}
                                            onResolve={onResolve}
                                          />
                                        );
                                      })()}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );})}
                    {isStreaming && (
                      <div className="typing-indicator-wrapper">
                        <TypingIndicator visible message="Thinking" isError={Boolean(messagesError)} isDisconnected={false} />
                      </div>
                    )}
                    {!isStreaming && (connectionError || Boolean(messagesError)) && (
                      <div className="typing-indicator-wrapper">
                        <TypingIndicator visible message={(messagesError as any)?.message || (connectionError ? 'Request failed. Try shortening your message or retry.' : 'There was an issue fetching messages.')} isError isDisconnected={false} />
                      </div>
                    )}
                  </div>
                </div>
                  <div className="lane left-lane" style={{ paddingBottom: '1rem', position: 'relative', zIndex: 10 }}>
                  <ChatInputBar 
                    onSubmit={handleSubmit} 
                    placeholder={mainPlaceholder} 
                    enableTools 
                    disabled={!canType} 
                    sendDisabled={mainSendLocked}
                    data-debug-disabled={!canType} 
                    data-debug-thread 
                    data-debug-connected={!connectionError} 
                    isEmptyState={messagesCount === 0} 
                    isCentered={messagesCount === 0} 
                    onFullScreenChange={setIsInputFullScreen}
                    containerStyle={{ ['--composer-min-height' as any]: '48px', ['--color-chat-input-bg' as any]: 'var(--color-bg-secondary)', ['--color-chat-input-border' as any]: 'var(--color-border)' }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </ArtifactLayout>
      {/* Split panel wired above */}
    </ChatLayout>
  );
});

ChatContent.displayName = 'ChatContent';

const ChatPageV2: React.FC = () => {
  return (
    <ChatProviderWrapper>
      <ChatContent />
    </ChatProviderWrapper>
  );
};

export default ChatPageV2;
